import { HttpError } from "./errors";
import { tryMock } from "./mockServer";

const USE_MOCKS = (process.env.REACT_APP_USE_MOCKS || "false") === "true";
const MOCK_FALLBACK_ON_NETWORK_FAIL = (process.env.REACT_APP_MOCK_FALLBACK || "false") === "true";
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");
const MERCHANT_DEBUG_KEY = (process.env.REACT_APP_MERCHANT_DEBUG_KEY || "").trim();

function isAbsoluteUrl(s: string): boolean {
  return /^https?:\/\//i.test(s);
}

function normalizeUrl(path: string): string {
  if (!path) return "/";

  // keep absolute URLs
  if (isAbsoluteUrl(path)) return path;

  // force leading slash
  const normalized = path.startsWith("/") ? path : `/${path}`;

  // prepend API base URL if configured
  return API_BASE_URL ? `${API_BASE_URL}${normalized}` : normalized;
}

/**
 * Get auth token from sessionStorage (portal or merchant).
 */
function getAuthToken(): string | null {
  try {
    // Check for portal session token first
    const portalRaw = localStorage.getItem("salla_returns_portal_session_v1");
    if (portalRaw) {
      const ps = JSON.parse(portalRaw);
      if (ps?.sessionToken) return ps.sessionToken;
    }
    // Check for merchant session token
    const merchantToken = sessionStorage.getItem("salla_returns_merchant_token");
    if (merchantToken) return merchantToken;
  } catch {
    // ignore parse errors
  }
  return null;
}

export async function httpJson<T>(path: string, init?: RequestInit): Promise<T> {
  const url = normalizeUrl(path);

  // 1) Use mocks fully if enabled
  if (USE_MOCKS) {
    const mocked = await tryMock(url, init);
    if (mocked) return (await mocked.json()) as T;
  }

  // 2) Build headers with auth
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init?.headers as Record<string, string> || {}),
  };

  const token = getAuthToken();
  if (token && !headers["Authorization"]) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Dev: send merchant debug key for testing without OAuth
  if (MERCHANT_DEBUG_KEY && !headers["X-Merchant-Debug-Key"]) {
    headers["X-Merchant-Debug-Key"] = MERCHANT_DEBUG_KEY;
  }

  // 3) Hit the network
  try {
    const res = await fetch(url, {
      ...init,
      headers,
    });

    if (!res.ok) {
      let body: unknown = undefined;
      try {
        const ct = res.headers.get("content-type") || "";
        body = ct.includes("application/json") ? await res.json() : await res.text();
      } catch {
        body = undefined;
      }

      if (MOCK_FALLBACK_ON_NETWORK_FAIL) {
        const mocked = await tryMock(url, init);
        if (mocked) return (await mocked.json()) as T;
      }

      throw new HttpError({
        message: `Request failed: ${res.status} ${res.statusText}`,
        status: res.status,
        url,
        body,
      });
    }

    if (res.status === 204) return undefined as T;

    const ct = res.headers.get("content-type") || "";
    if (!ct.includes("application/json")) {
      const text = await res.text();
      return text as unknown as T;
    }

    return (await res.json()) as T;
  } catch (e) {
    // Network error (ECONNREFUSED, etc.)
    if (MOCK_FALLBACK_ON_NETWORK_FAIL) {
      const mocked = await tryMock(url, init);
      if (mocked) return (await mocked.json()) as T;
    }
    throw e;
  }
}

/**
 * Upload a file via multipart/form-data (no JSON content-type).
 */
export async function httpUpload<T>(path: string, formData: FormData): Promise<T> {
  const url = normalizeUrl(path);
  const headers: Record<string, string> = {};

  const token = getAuthToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(url, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { body = undefined; }
    throw new HttpError({
      message: `Upload failed: ${res.status} ${res.statusText}`,
      status: res.status,
      url,
      body,
    });
  }

  return (await res.json()) as T;
}
